import { PaginatedResponse } from "@elit-hair/elit-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { callSvcAxiosInstance } from "@/constants";
import { incidentEndpoints } from "@/constants/endpoints";
import { DEFAULT_EMPTY_PAGINATION } from "@/constants/pagination";
import type { IncidentAudioDevicesSnapshot } from "@/pages/incidents/utils/collect-incident-data";
import {
    INCIDENT_REPORT_STATUS_FILTER,
    IncidentAnalytics,
    IncidentAnalyticsFilters,
    IncidentReport,
    IncidentReportFilters,
    PhoneNumberImportIncidentData,
} from "@/types/incident.types";
import type { AudioDeviceEvent } from "@/utils/audio-device-event-log.utils";

export type CreateIncidentPayload = {
    description: string;
    type?: string;
    data?: {
        "local-storage"?: Record<string, string>;
        "audio-devices"?: IncidentAudioDevicesSnapshot;
        "audio-device-log"?: AudioDeviceEvent[];
        // Filed automatically when someone picks an office seat that is already taken
        "seat-conflict"?: { seat: string; occupiedByUserId: string; requestedForUserId?: string };
        "phone-number-import"?: PhoneNumberImportIncidentData;
    };
};

export type UpdateIncidentPayload = {
    id: string;
    status?: IncidentReport["status"];
    type?: string;
};

export const incidentKeys = {
    all: ["incidents"] as const,
    lists: () => [...incidentKeys.all, "list"] as const,
    list: (filters: IncidentReportFilters) => [...incidentKeys.lists(), filters] as const,
    analytics: () => [...incidentKeys.all, "analytics"] as const,
    analyticsWithFilters: (filters: IncidentAnalyticsFilters) => [...incidentKeys.analytics(), filters] as const,
};

const getIncidentsRequest = (filters: IncidentReportFilters): Promise<PaginatedResponse<IncidentReport>> =>
    callSvcAxiosInstance
        .get<PaginatedResponse<IncidentReport>>(incidentEndpoints.getIncidents, { params: filters })
        .then((res) => res.data ?? { data: [], pagination: DEFAULT_EMPTY_PAGINATION });

export const useGetIncidents = (
    filters: IncidentReportFilters = {},
    options?: { enabled?: boolean; refetchInterval?: number }
) =>
    useQuery({
        queryKey: incidentKeys.list(filters),
        queryFn: () => getIncidentsRequest(filters),
        enabled: options?.enabled ?? true,
        refetchInterval: options?.refetchInterval,
    });

const ACTIVE_INCIDENTS_LIMIT = 100;
const OPEN_INCIDENTS_REFETCH_INTERVAL_MS = 60_000;

/**
 * For every user with an incident created in the last `hours` that is still open (reported or
 * assigned): the most recent of those incidents. One request for the newest active incidents.
 */
const getLatestOpenIncidentsRequest = async (hours: number): Promise<Record<string, IncidentReport>> => {
    const since = DateTime.now().minus({ hours });
    // The API filters whole days, so ask from the day before and trim to the window here
    const start = since.minus({ days: 1 }).toISODate() ?? undefined;
    const { data } = await getIncidentsRequest({
        status: INCIDENT_REPORT_STATUS_FILTER.ACTIVE,
        start,
        limit: ACTIVE_INCIDENTS_LIMIT,
    });

    // Newest first, so the first incident seen for a user is their latest
    const latest: Record<string, IncidentReport> = {};
    for (const incident of data) {
        if (DateTime.fromISO(incident.createdAt) < since) continue;
        latest[incident.userId] ??= incident;
    }
    return latest;
};

const toIncidentMap = (incidents: Record<string, IncidentReport>) => new Map(Object.entries(incidents));

/** User id → their latest incident from the last `hours` that is still open. */
export const useLatestOpenIncidents = (hours: number, options?: { enabled?: boolean }) =>
    useQuery({
        queryKey: [...incidentKeys.lists(), "latest-open", hours] as const,
        queryFn: () => getLatestOpenIncidentsRequest(hours),
        select: toIncidentMap,
        refetchInterval: OPEN_INCIDENTS_REFETCH_INTERVAL_MS,
        enabled: options?.enabled ?? true,
    });

const getIncidentAnalyticsRequest = (filters: IncidentAnalyticsFilters): Promise<IncidentAnalytics> =>
    callSvcAxiosInstance
        .get<IncidentAnalytics>(incidentEndpoints.getIncidentAnalytics, { params: filters })
        .then((res) => res.data);

export const useGetIncidentAnalytics = (filters: IncidentAnalyticsFilters = {}, options?: { enabled?: boolean }) =>
    useQuery({
        queryKey: incidentKeys.analyticsWithFilters(filters),
        queryFn: () => getIncidentAnalyticsRequest(filters),
        enabled: options?.enabled ?? true,
    });

const createIncidentRequest = (payload: CreateIncidentPayload): Promise<IncidentReport> =>
    callSvcAxiosInstance.post<IncidentReport>(incidentEndpoints.createIncident, payload).then((res) => res.data);

export const useCreateIncident = () =>
    useMutation({
        mutationFn: createIncidentRequest,
    });

const updateIncidentRequest = ({ id, ...payload }: UpdateIncidentPayload): Promise<IncidentReport> =>
    callSvcAxiosInstance.patch<IncidentReport>(incidentEndpoints.updateIncident(id), payload).then((res) => res.data);

export const useUpdateIncident = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateIncidentRequest,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
            queryClient.invalidateQueries({ queryKey: incidentKeys.analytics() });
        },
    });
};

type RemoveIncidentReviewerPayload = {
    id: string;
    reviewerId: string;
};

const removeIncidentReviewerRequest = ({ id, reviewerId }: RemoveIncidentReviewerPayload): Promise<IncidentReport> =>
    callSvcAxiosInstance
        .delete<IncidentReport>(incidentEndpoints.removeReviewer(id, reviewerId))
        .then((res) => res.data);

export const useRemoveIncidentReviewer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeIncidentReviewerRequest,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
        },
    });
};
